import React, { useState, useEffect } from 'react';
import { 
  Search, Edit, Save, X, Users, Code, BookOpen, RefreshCw, 
  ChevronDown, ChevronUp, Check, AlertCircle, Loader, Download
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const HackathonParticipantManager = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [themeFilter, setThemeFilter] = useState('all');
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('');
  const [exporting, setExporting] = useState(false);

  // Standard Hackathon 2nd Edition themes (new valid values)
  const standardThemes = ["Artificial Intelligence", "Blockchain", "Open Innovation"];

  // Dynamically collect all unique themes from teams (including legacy themes)
  const allExistingThemes = [...new Set(teams.map(t => t.hackathon_theme).filter(Boolean))];
  
  // Combine standard themes + any legacy themes for the filter dropdown
  const filterThemes = [...new Set([...standardThemes, ...allExistingThemes])].sort();
  
  // Only allow updating to valid new themes
  const hackathonThemes = standardThemes;

  const workshopOptions = [
    { value: "AI", label: "AI Workshop" },
    { value: "Data Science", label: "Data Science Workshop" },
    { value: "Ethical Hacking", label: "Ethical Hacking Workshop" },
    { value: null, label: "No Workshop" }
  ];

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/registrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(res.data.teams || []);
    } catch (err) {
      toast.error('Failed to fetch teams');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async (exportType = 'teams') => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/api/admin/registrations/export-csv`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          payment_status: 'all',
          theme: themeFilter !== 'all' ? themeFilter : undefined
        }
      });
      
      const data = exportType === 'members' ? res.data.members_csv : res.data.teams_csv;
      
      if (data.length === 0) {
        toast.info('No data to export');
        return;
      }
      
      // Convert to CSV
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => 
          headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(',')
        )
      ].join('\n');
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `hackathon_${exportType}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      toast.success(`Exported ${data.length} ${exportType}`);
    } catch (err) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleUpdateTheme = async (teamId, newTheme) => {
    if (!newTheme) {
      toast.error('Please select a theme');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/api/admin/team/update-theme`,
        { team_id: String(teamId), new_theme: newTheme },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Theme updated successfully');
      setEditingTeam(null);
      setSelectedTheme('');
      // Refresh the teams list to show updated data
      await fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update theme');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateWorkshop = async (teamId, memberEmail, newWorkshop) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/admin/team/update-member-workshop`,
        { team_id: teamId, member_email: memberEmail, new_workshop: newWorkshop },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Workshop updated successfully');
      setEditingMember(null);
      fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update workshop');
    } finally {
      setSaving(false);
    }
  };

  const filteredTeams = teams.filter(team => {
    const matchesSearch = 
      team.team_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(team.registration_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      team.members?.some(m => 
        m.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    
    const matchesTheme = themeFilter === 'all' || team.hackathon_theme === themeFilter;
    
    return matchesSearch && matchesTheme;
  });

  const getThemeColor = (theme) => {
    switch (theme) {
      case 'AI': 
      case 'Artificial Intelligence': 
      case 'Generative AI':
        return 'bg-purple-100 text-purple-700 border-purple-300';
      case 'Blockchain': 
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'Open Innovation': 
        return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'AgriTech':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'Education':
        return 'bg-cyan-100 text-cyan-700 border-cyan-300';
      default: 
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getWorkshopColor = (workshop) => {
    switch (workshop) {
      case 'AI': return 'bg-purple-50 text-purple-600';
      case 'Data Science': return 'bg-cyan-50 text-cyan-600';
      case 'Ethical Hacking': return 'bg-red-50 text-red-600';
      default: return 'bg-gray-50 text-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="animate-spin text-purple-600 mr-2" size={24} />
        <span className="text-gray-600">Loading participants...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Users size={28} />
          Hackathon 2nd Edition - Participant Manager
        </h2>
        <p className="text-purple-200 mt-1">
          Manage team themes and member workshop selections
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by team name, registration number, or member email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              data-testid="search-input"
            />
          </div>

          {/* Theme Filter */}
          <div className="w-full md:w-64">
            <select
              value={themeFilter}
              onChange={(e) => setThemeFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              data-testid="theme-filter"
            >
              <option value="all">All Themes</option>
              {filterThemes.map(theme => (
                <option key={theme} value={theme}>{theme}</option>
              ))}
              <option value="">Not Set</option>
            </select>
          </div>

          {/* Refresh */}
          <button
            onClick={fetchTeams}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2 text-gray-700 transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
          
          {/* Export Buttons */}
          <button
            onClick={() => handleExportCSV('teams')}
            disabled={exporting}
            className="px-4 py-2 bg-green-100 hover:bg-green-200 rounded-lg flex items-center gap-2 text-green-700 transition-colors disabled:opacity-50"
            data-testid="export-teams-btn"
          >
            <Download size={18} />
            {exporting ? 'Exporting...' : 'Export Teams'}
          </button>
          <button
            onClick={() => handleExportCSV('members')}
            disabled={exporting}
            className="px-4 py-2 bg-blue-100 hover:bg-blue-200 rounded-lg flex items-center gap-2 text-blue-700 transition-colors disabled:opacity-50"
            data-testid="export-members-btn"
          >
            <Download size={18} />
            {exporting ? 'Exporting...' : 'Export Members'}
          </button>
        </div>

        {/* Stats */}
        <div className="flex gap-4 mt-4 text-sm">
          <span className="text-gray-500">
            Showing <span className="font-semibold text-gray-700">{filteredTeams.length}</span> of {teams.length} teams
          </span>
        </div>
      </div>

      {/* Teams List */}
      <div className="space-y-3">
        {filteredTeams.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-8 text-center">
            <Users className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">No teams found matching your criteria</p>
          </div>
        ) : (
          filteredTeams.map((team) => (
            <div 
              key={team.registration_number || team.id}
              className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
              data-testid={`team-card-${team.registration_number}`}
            >
              {/* Team Header */}
              <div 
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedTeam(expandedTeam === team.registration_number ? null : team.registration_number)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center text-white font-bold">
                      {team.team_name?.charAt(0) || 'T'}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{team.team_name || 'Unnamed Team'}</h3>
                      <p className="text-sm text-gray-500">
                        {team.registration_number} • {team.total_members || team.members?.length || 0} members
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Current Theme Badge */}
                    {editingTeam === team.registration_number ? (
                      <div className="flex items-center gap-2">
                        <select
                          value={selectedTheme || team.hackathon_theme || ''}
                          onChange={(e) => setSelectedTheme(e.target.value)}
                          disabled={saving}
                          className="px-3 py-1.5 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          onClick={(e) => e.stopPropagation()}
                          data-testid="theme-select"
                        >
                          <option value="" disabled>Select Theme</option>
                          {hackathonThemes.map(theme => (
                            <option key={theme} value={theme}>{theme}</option>
                          ))}
                        </select>
                        <button
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            if (selectedTheme) {
                              handleUpdateTheme(team.registration_number, selectedTheme);
                            }
                          }}
                          disabled={saving || !selectedTheme}
                          className="p-1.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-lg disabled:opacity-50"
                          title="Save Theme"
                        >
                          {saving ? <Loader className="animate-spin" size={18} /> : <Save size={18} />}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingTeam(null); setSelectedTheme(''); }}
                          className="p-1.5 text-gray-400 hover:text-gray-600"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getThemeColor(team.hackathon_theme)}`}>
                          <Code size={14} className="inline mr-1" />
                          {team.hackathon_theme || 'Not Set'}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingTeam(team.registration_number); setSelectedTheme(team.hackathon_theme || ''); }}
                          className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Edit Theme"
                          data-testid="edit-theme-btn"
                        >
                          <Edit size={16} />
                        </button>
                      </div>
                    )}

                    {/* Expand/Collapse */}
                    <div className="text-gray-400">
                      {expandedTeam === team.registration_number ? (
                        <ChevronUp size={20} />
                      ) : (
                        <ChevronDown size={20} />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded Members Section */}
              {expandedTeam === team.registration_number && (
                <div className="border-t border-gray-200 bg-gray-50 p-4">
                  <h4 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                    <BookOpen size={16} />
                    Team Members & Workshop Selection
                  </h4>
                  
                  <div className="space-y-2">
                    {team.members?.map((member, idx) => (
                      <div 
                        key={member.email || idx}
                        className="bg-white rounded-lg p-3 border border-gray-200 flex items-center justify-between"
                        data-testid={`member-row-${idx}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 text-sm font-medium">
                            {member.name?.charAt(0) || 'M'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{member.name || 'Member'}</p>
                            <p className="text-xs text-gray-500">{member.email}</p>
                          </div>
                          {member.is_leader && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                              Leader
                            </span>
                          )}
                        </div>

                        {/* Workshop Selection */}
                        <div className="flex items-center gap-2">
                          {editingMember === `${team.registration_number}-${member.email}` ? (
                            <div className="flex items-center gap-2">
                              <select
                                defaultValue={member.workshop_choice || ''}
                                onChange={(e) => {
                                  const value = e.target.value === '' ? null : e.target.value;
                                  handleUpdateWorkshop(team.registration_number, member.email, value);
                                }}
                                disabled={saving}
                                className="px-3 py-1.5 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                data-testid="workshop-select"
                              >
                                {workshopOptions.map(opt => (
                                  <option key={opt.label} value={opt.value || ''}>{opt.label}</option>
                                ))}
                              </select>
                              <button
                                onClick={() => setEditingMember(null)}
                                className="p-1.5 text-gray-400 hover:text-gray-600"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getWorkshopColor(member.workshop_choice)}`}>
                                {member.workshop_choice || 'No Workshop'}
                              </span>
                              <button
                                onClick={() => setEditingMember(`${team.registration_number}-${member.email}`)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit Workshop"
                                data-testid="edit-workshop-btn"
                              >
                                <Edit size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Admin Edit History */}
                  {(team.theme_updated_by_admin || team.members?.some(m => m.workshop_updated_by_admin)) && (
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <AlertCircle size={12} />
                        Some values were modified by admin
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default HackathonParticipantManager;
